/**
 * <ambient-flow> — fond animé « voile lumineux » en WebGL.
 * Adaptation du composant Auralis : mêmes nappes de bruit, même mouvement et
 * même grain, mais sur une base claire (blanc et blanc cassé) au lieu du noir.
 * - Aucune dépendance externe.
 * - L'animation s'arrête hors écran et quand l'onglet est masqué.
 * - Si le visiteur a demandé à réduire les animations, une image fixe est rendue.
 * - Sans WebGL, le dégradé CSS de repli reste affiché.
 */
(() => {
  'use strict';

  if (customElements.get('ambient-flow')) return;

  const VERTEX = `
    attribute vec2 position;
    varying vec2 vUv;
    void main() {
      vUv = position * 0.5 + 0.5;
      gl_Position = vec4(position, 0.0, 1.0);
    }
  `;

  const FRAGMENT = `
    #ifdef GL_FRAGMENT_PRECISION_HIGH
      precision highp float;
    #else
      precision mediump float;
    #endif
    varying vec2 vUv;

    uniform vec2  u_resolution;
    uniform float u_time;
    uniform float u_grain;
    uniform vec3  u_base;
    uniform vec3  u_color1;
    uniform vec3  u_color2;

    vec3 mod289(vec3 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
    vec2 mod289(vec2 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
    vec3 permute(vec3 x) { return mod289(((x * 34.0) + 1.0) * x); }

    float snoise(vec2 v) {
      const vec4 C = vec4(0.211324865405187, 0.366025403784439, -0.577350269189626, 0.024390243902439);
      vec2 i  = floor(v + dot(v, C.yy));
      vec2 x0 = v - i + dot(i, C.xx);
      vec2 i1 = (x0.x > x0.y) ? vec2(1.0, 0.0) : vec2(0.0, 1.0);
      vec4 x12 = x0.xyxy + C.xxzz;
      x12.xy -= i1;
      i = mod289(i);
      vec3 p = permute(permute(i.y + vec3(0.0, i1.y, 1.0)) + i.x + vec3(0.0, i1.x, 1.0));
      vec3 m = max(0.5 - vec3(dot(x0, x0), dot(x12.xy, x12.xy), dot(x12.zw, x12.zw)), 0.0);
      m = m * m; m = m * m;
      vec3 x = 2.0 * fract(p * C.www) - 1.0;
      vec3 h = abs(x) - 0.5;
      vec3 ox = floor(x + 0.5);
      vec3 a0 = x - ox;
      m *= 1.79284291400159 - 0.85373472095314 * (a0 * a0 + h * h);
      vec3 g;
      g.x  = a0.x  * x0.x  + h.x  * x0.y;
      g.yz = a0.yz * x12.xz + h.yz * x12.yw;
      return 130.0 * dot(m, g);
    }

    void main() {
      vec2 uv = vUv;
      float ratio = u_resolution.x / u_resolution.y;
      vec2 p = uv * vec2(ratio, 1.0);
      float t = u_time * 0.2;

      // Mêmes nappes que l'original
      float n1 = snoise(p * 0.5 + t);
      float n2 = snoise(p * 0.9 - t * 0.5 + n1);
      float light = pow(abs(n2), 2.5) * 0.5;

      // Base claire : la couleur se mélange au blanc au lieu de s'ajouter au noir
      vec3 col = u_base;
      // (coefficients un peu plus élevés que sur fond noir pour que le rouge reste franc)
      col = mix(col, u_color1, clamp(smoothstep(0.1, 1.0, n1) * 0.72, 0.0, 1.0));
      col = mix(col, u_color2, clamp(light * 1.3, 0.0, 1.0));

      // Grain de pellicule
      float grain = fract(sin(dot(uv, vec2(12.9898, 78.233))) * 43758.5453 + u_time);
      col *= 1.0 + (grain - 0.5) * u_grain * 0.35;

      // Vignette : les bords s'estompent vers un blanc cassé (et non vers le noir)
      float dist = length(uv - 0.5);
      vec3 edge = u_base * vec3(0.985, 0.968, 0.955);
      col = mix(edge, col, smoothstep(1.2, 0.2, dist));

      gl_FragColor = vec4(col, 1.0);
    }
  `;

  const hexToRgb = (hex, fallback) => {
    const match = /^#?([0-9a-f]{6})$/i.exec((hex || '').trim());
    if (!match) return fallback;
    const value = parseInt(match[1], 16);
    return [((value >> 16) & 255) / 255, ((value >> 8) & 255) / 255, (value & 255) / 255];
  };

  class AmbientFlow extends HTMLElement {
    connectedCallback() {
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.visible = false;
      this.frame = 0;
      this.elapsed = 0;
      this.last = 0;

      const speed = parseFloat(this.dataset.speed);
      // 0.3 est la vitesse d'origine du composant ; le réglage du thème la module
      this.speed = (Number.isNaN(speed) ? 1 : Math.max(0, speed)) * 0.3;
      const grain = parseFloat(this.dataset.grain);
      this.grain = Number.isNaN(grain) ? 0.2 : Math.min(1, Math.max(0, grain));
      this.maxRatio = Math.max(0.5, parseFloat(this.dataset.maxRatio) || 1);

      if (!this.setup()) return;

      this.render = this.render.bind(this);
      this.onMotionChange = () => this.toggle();

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => {
          this.resize();
          this.draw();
        });
        this.resizeObserver.observe(this);
      }

      if ('IntersectionObserver' in window) {
        this.intersectionObserver = new IntersectionObserver(([entry]) => {
          this.visible = entry.isIntersecting;
          this.toggle();
        });
        this.intersectionObserver.observe(this);
      } else {
        this.visible = true;
      }

      if (typeof this.reduceMotion.addEventListener === 'function') {
        this.reduceMotion.addEventListener('change', this.onMotionChange);
      }

      this.resize();
      // Image de départ non uniforme, utile aussi quand l'animation est figée
      this.elapsed = 9;
      this.draw();
      this.toggle();
    }

    disconnectedCallback() {
      this.stop();
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.intersectionObserver) this.intersectionObserver.disconnect();
      if (this.reduceMotion && typeof this.reduceMotion.removeEventListener === 'function') {
        this.reduceMotion.removeEventListener('change', this.onMotionChange);
      }
      if (this.gl) {
        const extension = this.gl.getExtension('WEBGL_lose_context');
        if (extension) extension.loseContext();
      }
      if (this.canvas) this.canvas.remove();
      this.classList.remove('is-live');
      this.gl = null;
      this.canvas = null;
    }

    setup() {
      const canvas = document.createElement('canvas');
      const options = { alpha: false, antialias: false, depth: false, stencil: false, powerPreference: 'low-power' };
      let gl = null;
      try {
        gl = canvas.getContext('webgl', options) || canvas.getContext('experimental-webgl', options);
      } catch (error) {
        gl = null;
      }
      if (!gl) return false;

      const compile = (type, source) => {
        const shader = gl.createShader(type);
        gl.shaderSource(shader, source);
        gl.compileShader(shader);
        if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
          gl.deleteShader(shader);
          return null;
        }
        return shader;
      };

      const vertex = compile(gl.VERTEX_SHADER, VERTEX);
      const fragment = compile(gl.FRAGMENT_SHADER, FRAGMENT);
      if (!vertex || !fragment) return false;

      const program = gl.createProgram();
      gl.attachShader(program, vertex);
      gl.attachShader(program, fragment);
      gl.linkProgram(program);
      if (!gl.getProgramParameter(program, gl.LINK_STATUS)) return false;
      gl.useProgram(program);

      const buffer = gl.createBuffer();
      gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
      gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
      const position = gl.getAttribLocation(program, 'position');
      gl.enableVertexAttribArray(position);
      gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);

      this.uniforms = {
        resolution: gl.getUniformLocation(program, 'u_resolution'),
        time: gl.getUniformLocation(program, 'u_time'),
      };

      gl.uniform1f(gl.getUniformLocation(program, 'u_grain'), this.grain);
      gl.uniform3fv(gl.getUniformLocation(program, 'u_base'), hexToRgb(this.dataset.base, [1, 1, 1]));
      gl.uniform3fv(gl.getUniformLocation(program, 'u_color1'), hexToRgb(this.dataset.color1, [0.937, 0.267, 0.267]));
      gl.uniform3fv(gl.getUniformLocation(program, 'u_color2'), hexToRgb(this.dataset.color2, [0.863, 0.149, 0.149]));

      canvas.addEventListener('webglcontextlost', (event) => {
        event.preventDefault();
        this.stop();
        this.classList.remove('is-live');
      });

      this.gl = gl;
      this.canvas = canvas;
      this.appendChild(canvas);
      this.classList.add('is-live');
      return true;
    }

    resize() {
      if (!this.gl) return;
      // Nappes douces : une densité réduite suffit et ménage la batterie
      this.ratio = Math.min(window.devicePixelRatio || 1, this.maxRatio);
      const width = Math.max(1, Math.round(this.clientWidth * this.ratio));
      const height = Math.max(1, Math.round(this.clientHeight * this.ratio));
      if (this.canvas.width === width && this.canvas.height === height) return;
      this.canvas.width = width;
      this.canvas.height = height;
      this.gl.viewport(0, 0, width, height);
    }

    draw() {
      const gl = this.gl;
      if (!gl || gl.isContextLost()) return;
      gl.uniform2f(this.uniforms.resolution, this.canvas.width, this.canvas.height);
      gl.uniform1f(this.uniforms.time, this.elapsed);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    }

    render(now) {
      if (!this.frame) return;
      const delta = this.last ? Math.min(now - this.last, 100) : 0;
      this.last = now;
      this.elapsed += (delta / 1000) * this.speed;
      this.draw();
      this.frame = requestAnimationFrame(this.render);
    }

    toggle() {
      const shouldRun = this.visible && this.speed > 0 && !this.reduceMotion.matches;
      if (shouldRun) this.start();
      else this.stop();
    }

    start() {
      if (this.frame || !this.gl) return;
      this.last = 0;
      this.frame = requestAnimationFrame(this.render);
    }

    stop() {
      if (this.frame) cancelAnimationFrame(this.frame);
      this.frame = 0;
    }
  }

  customElements.define('ambient-flow', AmbientFlow);
})();
